'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/Button';
import { X, Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import { useEnquiries } from '@/hooks/useEnquiries';
import { cn } from '@/lib/utils';

interface MessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional recipient name to display */
  recipientName?: string;
  /** Optional subject line */
  defaultSubject?: string;
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export const MessageModal: React.FC<MessageModalProps> = ({
  isOpen,
  onClose,
  recipientName = 'Our Team',
  defaultSubject = 'General Enquiry from Dashboard',
}) => {
  const { customer, isAuthenticated } = useCustomerAuth();
  const { createEnquiry } = useEnquiries();
  
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Delay reset to allow close animation
      const timer = setTimeout(() => {
        setMessage('');
        setStatus('idle');
        setErrorMessage('');
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) return;
    if (!isAuthenticated || !customer) {
      setErrorMessage('Please log in to send a message.');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      // Create an enquiry in Route Royal
      const enquiry = await createEnquiry({
        subject: defaultSubject,
        message: message.trim(),
      });

      if (!enquiry) {
        throw new Error('Failed to create enquiry');
      }

      setStatus('success');
      
      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (err) {
      console.error('[MessageModal] Send failed:', err);
      setErrorMessage(err instanceof Error ? err.message : 'Failed to send message. Please try again.');
      setStatus('error');
    }
  };

  const handleClose = () => {
    if (status !== 'submitting') {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && status !== 'submitting') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, status]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-[fadeIn_0.2s_ease-out]"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-white rounded-3xl shadow-2xl w-full max-w-lg pointer-events-auto animate-[fadeIn_0.2s_ease-out,slideUp_0.3s_ease-out]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-stone-100">
            <div>
              <h2 className="text-xl font-bold text-stone-900">Send a Message</h2>
              <p className="text-sm text-stone-500 mt-0.5">to {recipientName}</p>
            </div>
            <button
              onClick={handleClose}
              disabled={status === 'submitting'}
              className="p-2 text-stone-400 hover:text-stone-900 hover:bg-stone-100 rounded-full transition-colors disabled:opacity-50"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit}>
            <div className="p-6">
              {/* User Info Display */}
              {customer && (
                <div className="mb-4 p-4 bg-stone-50 rounded-2xl border border-stone-100">
                  <p className="text-xs text-stone-500 uppercase tracking-wider font-medium mb-2">Sending as</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-stone-900 text-white flex items-center justify-center text-sm font-semibold">
                      {(customer.firstName?.[0] || customer.email?.[0] || 'U').toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-stone-900">
                        {customer.firstName && customer.lastName 
                          ? `${customer.firstName} ${customer.lastName}`
                          : customer.displayName || customer.email}
                      </p>
                      <p className="text-xs text-stone-500">{customer.email}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Success State */}
              {status === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-stone-900 mb-2">Message Sent!</h3>
                  <p className="text-stone-500 text-sm">
                    We&apos;ll get back to you as soon as possible.
                  </p>
                </div>
              ) : (
                <>
                  {/* Error State */}
                  {status === 'error' && errorMessage && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-3">
                      <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Failed to send</p>
                        <p className="text-xs text-red-600 mt-0.5">{errorMessage}</p>
                      </div>
                    </div>
                  )}

                  {/* Message Textarea */}
                  <div>
                    <label 
                      htmlFor="message" 
                      className="block text-sm font-medium text-stone-700 mb-2"
                    >
                      Your Message
                    </label>
                    <textarea
                      ref={textareaRef}
                      id="message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="How can we help you today?"
                      rows={5}
                      disabled={status === 'submitting'}
                      className={cn(
                        "w-full px-4 py-3 rounded-2xl border border-stone-200 text-stone-900 placeholder:text-stone-400",
                        "focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400",
                        "resize-none transition-all disabled:opacity-50 disabled:bg-stone-50"
                      )}
                    />
                    <p className="text-xs text-stone-400 mt-2">
                      Your message will be sent to our sales team and logged in your account.
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            {status !== 'success' && (
              <div className="flex items-center justify-end gap-3 p-6 pt-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={status === 'submitting'}
                  className="!rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!message.trim() || status === 'submitting'}
                  className="!bg-stone-900 hover:!bg-stone-800 !text-white !rounded-xl min-w-[120px]"
                >
                  {status === 'submitting' ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Animation styles */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default MessageModal;
